import { useMemoizedContentGetter } from 'utils/contentful'
import Section from 'components/Section'
import { Typography, MediaImageStandard } from '@/components/Contentful'
import { memo } from 'react'
import { Carousel } from 'components/Carousels'

const ItemComponentCarousel = memo(function ItemComponentCarousel({ item }) {
  return (
    <div className="grid bg-neutral-0 rounded-2xl overflow-hidden border border-solid border-border-primary h-[479px]">
      <div className="p-6 pt-8 pb-4 flex flex-col gap-2 flex-1">
        {item.icon && (
          <MediaImageStandard
            content={{ url: item.icon }}
            layout="fill"
            objectFit="cover"
            width={48}
            height={48}
            fillRelativeWrapper={false}
            alt=""
          />
        )}
        {item.title && <p className="headingMd font-bold text-content-primary m-0">{item.title}</p>}
        {item.subTitle && (
          <p className="headingSm text-content-primary font-normal m-0">{item.subTitle}</p>
        )}
        {item.tags?.length > 0 && (
          <div className="flex flex-col gap-2 pt-2">
            {item.tags.map((tag, j) => (
              <span
                key={j}
                className=" rounded-full px-3 py-1 bodySm text-content-primary bg-blue-50 w-fit"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
      {item.image && (
        <div>
          <MediaImageStandard
            content={{ url: item.image }}
            objectFit="cover"
            width={536}
            height={240}
            className="object-top"
            fillRelativeWrapper={false}
            alt={item.title}
          />
        </div>
      )}
    </div>
  )
})

const MultiProductSectionModule = memo(function MultiProductSectionModule({ config }) {
  const { title, subTitle, items, showIcons } = config || {}
  return (
    <Section
      disableGutters={true}
      data-testid="multi-product-section"
      className="py-12 sm:py-20"
      childContainerClassName="pl-5 pr-0 md:px-8"
    >
      <div className="grid grid-cols-4 gap-x-8 gap-y-6 breakpointMd:grid-cols-6 breakpointLg:grid-cols-12 breakpointLg:gap-x-8 breakpointLg:gap-y-0 breakpointXl:grid-cols-12 breakpointXl:gap-x-8">
        <div className="col-span-4 pr-5 md:pr-0 breakpointMd:col-span-6 breakpointLg:col-span-12 w-full mx-auto breakpointMd:max-w-[600px] breakpointLg:max-w-[768px] pb-2 breakpointLg:pb-6">
          {title && (
            <Typography
              content={title}
              variant="headingLg"
              className="headingLg font-bold sm:text-[52px]! m-auto leading-[120%]! text-left sm:text-center pb-4"
              data-testid="multi-product-title"
              component="h2"
            />
          )}
          {subTitle && (
            <Typography
              content={subTitle}
              variant="bodyLg"
              className="font-light text-left sm:text-center"
              data-testid="multi-product-subtitle"
              component="h3"
            />
          )}
        </div>
        {items?.map((item, i) => (
          <div
            key={i}
            className="hidden md:flex col-span-4 breakpointMd:col-span-2 breakpointLg:col-span-4 flex-col bg-neutral-0 rounded-2xl overflow-hidden border border-solid border-border-primary h-[479px]"
          >
            <div className="p-6 pt-8 pb-4 flex flex-col gap-2 flex-1">
              {showIcons === true && item.icon && (
                <MediaImageStandard
                  content={{ url: item.icon }}
                  layout="fill"
                  objectFit="cover"
                  width={48}
                  height={48}
                  fillRelativeWrapper={false}
                  alt=""
                />
              )}
              {item.title && (
                <p className="headingMd font-bold text-content-primary m-0">{item.title}</p>
              )}
              {item.subTitle && (
                <p className="headingSm text-content-primary font-normal m-0">{item.subTitle}</p>
              )}
              {item.tags?.length > 0 && (
                <div className="flex flex-col gap-2 pt-2">
                  {item.tags.map((tag, j) => (
                    <span
                      key={j}
                      className=" rounded-full px-3 py-1 bodySm text-content-primary bg-blue-50 w-fit"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {item.image && (
              <div>
                <MediaImageStandard
                  content={{ url: item.image }}
                  objectFit="cover"
                  width={536}
                  height={240}
                  fillRelativeWrapper={false}
                  objectPosition="top"
                  className="object-top"
                  alt={item.title}
                />
              </div>
            )}
          </div>
        ))}
        <div className="flex col-span-4 md:hidden -ml-5">
          <Carousel
            carouselItems={items}
            className="p-0"
            classNameContainer={'first:pl-0'}
            classNameItems="max-w-[310px] sm:max-w-[384px]"
            itemComponent={({ item }) => (
              <ItemComponentCarousel item={item} showIcons={showIcons} />
            )}
            variant="standard"
            idPrefix={`multi-product-section-carousel`}
            withIndicators={true}
            variationDot={config?.dotsOn ?? null}
            classNameIndicators={
              "[&_div]:h-2 [&_div]:w-2 [&_div]:bg-border-state-disabled [&_div]:opacity-100 [&_div[data-active='true']]:bg-content-primary! [&_div[data-active='true']]:w-[48px] [&_div[data-active='true']]:rounded-[8px] my-0 mt-4!"
            }
          />
        </div>
      </div>
    </Section>
  )
})

function MultiProductSection({ content }) {
  const { config } = useMemoizedContentGetter(content, ['config'])
  return <MultiProductSectionModule config={config?.jsonContent || {}} />
}

export { MultiProductSection, MultiProductSectionModule }
export default MultiProductSection
